import { useState, useRef, useEffect } from 'react';
import { X, Send, Brain, Sparkles, Diamond } from 'lucide-react';
import { usePage } from '../contexts/PageContext.jsx';

const PAGE_STARTERS = {
  'portfolio-command': [
    "What's your current thesis?",
    "Why did you flip Bitcoin to sell?",
    "What are you watching tonight?",
    "Which recommendation has highest conviction and why?",
  ],
  'macro-radar': [
    "What does the current news flow mean for my portfolio?",
    "Explain this regime in plain language",
    "What's the biggest risk right now?",
  ],
  'futures-signals': [
    "Should I trust the current session profile?",
    "Why isn't Active Signal firing?",
    "What's your read on the next NY session?",
  ],
  'signal-convergence': [
    "Why are most matrix rows empty?",
    "What's the strongest convergence signal right now?",
    "How should I read the capitulation detector?",
  ],
  'currency': [
    "What is your current monetary thesis?",
    "Why is the KORINTH Dollar Index falling?",
    "Which central bank is most likely to move next?",
    "Explain the current debasement signals",
  ],
  crypto: [
    "Which DCA targets are in accumulation zone right now?",
    "Any recent catalysts from BossmanCalls I should act on?",
    "What's driving the biggest crypto mover today?",
  ],
  themes: [
    "Which themes have the highest conviction right now?",
    "What tickers benefit most from my active themes?",
    "Are any themes close to invalidation?",
  ],
  credibility: [
    "Which sources have the highest 30d hit rate?",
    "Any credibility shifts in the last week?",
    "How does BossBot's credibility compare to ZeroHedge?",
  ],
  'signal-queue': [
    "What signals triggered emergency cycles today?",
    "Why was this signal priority L3?",
    "Show me the highest-priority pending signals",
  ],
  watchlist: [
    "Which sectors look strongest right now?",
    "Any watchlist tickers getting unusual news attention?",
    "What are the top picks for the Oil thesis?",
  ],
  'smart-money': [
    "What are the biggest institutional moves this quarter?",
    "Any suspicious congressional trades recently?",
    "Where does smart money disagree with your positions?",
  ],
  voices: [
    "Where are you contrarian vs the street right now?",
    "Which voices have the best track record?",
    "Is street consensus shifting on Gold?",
  ],
  telegram: [
    "What's the biggest Telegram signal right now?",
    "Which channels are producing the best signals today?",
    "Show me critical messages from the last hour",
  ],
  default: [
    "What's your current thesis?",
    "What should I pay attention to today?",
    "Summarize today so far",
  ],
};

export default function KairosGlobalChat() {
  const { page } = usePage();
  const [isOpen, setIsOpen] = useState(false);
  const [recContext, setRecContext] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [deep, setDeep] = useState(() => {
    try { return localStorage.getItem('kairos_deep_thinking') === 'true'; } catch { return false; }
  });
  const bottomRef = useRef(null);

  // Expose openWithContext globally so rec cards can trigger it
  useEffect(() => {
    window.__kairosOpenChat = (ctx) => { setRecContext(ctx); setIsOpen(true); };
    return () => { delete window.__kairosOpenChat; };
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    try { localStorage.setItem('kairos_deep_thinking', String(deep)); } catch {}
  }, [deep]);

  useEffect(() => {
    if (recContext && isOpen) {
      setMessages([{ role: 'assistant', content: `Ask me about ${recContext.asset} — conviction ${recContext.conviction_overall?.toFixed(1)}/10 ${recContext.direction}` }]);
    }
  }, [recContext, isOpen]);

  const starters = PAGE_STARTERS[page] || PAGE_STARTERS.default;

  async function sendMessage(text) {
    if (!text.trim()) return;
    const userMsg = { role: 'user', content: text.trim() };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/kairos/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          context: recContext ? { type: 'recommendation', id: recContext.id } : { type: 'page', page },
          history: newHistory.slice(-8),
          deepThinking: deep,
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply || 'No response.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Failed to reach KAIROS.' }]);
    } finally { setLoading(false); }
  }

  function renderContent(text) {
    if (!text) return '';
    return text.split(/(\[(?:obs|rec|pred):\d+\])/).map((part, i) => {
      const match = part.match(/\[(obs|rec|pred):(\d+)\]/);
      if (match) {
        const color = match[1] === 'obs' ? 'bg-blue-500/10 text-blue-400' : match[1] === 'rec' ? 'bg-korinth-accent/10 text-korinth-accent' : 'bg-purple-500/10 text-purple-400';
        return <span key={i} className={`text-[9px] font-mono px-1 py-0.5 rounded ${color} mx-0.5`}>{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  }

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button onClick={() => { setRecContext(null); setIsOpen(true); }}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-korinth-accent text-korinth-bg font-mono font-bold text-sm shadow-lg shadow-korinth-accent/20 hover:bg-korinth-accent/90 transition-all">
          <Brain className="w-4 h-4" /> Ask KAIROS
        </button>
      )}

      {/* Chat Drawer */}
      {isOpen && (
        <div className="fixed inset-y-0 right-0 w-96 max-w-full bg-korinth-surface border-l border-korinth-border z-50 flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-korinth-border">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-korinth-accent" />
              <span className="text-sm font-mono font-bold text-white">Ask KAIROS</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-korinth-muted hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          {/* Deep thinking toggle */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-korinth-border/50">
            <div className="flex items-center gap-1.5">
              <Diamond className={`w-3 h-3 ${deep ? 'text-purple-400' : 'text-korinth-muted'}`} />
              <span className="text-[10px] font-mono text-korinth-muted">Deep thinking (Opus)</span>
            </div>
            <button onClick={() => setDeep(!deep)}
              className={`w-8 h-4 rounded-full transition-colors ${deep ? 'bg-purple-500' : 'bg-korinth-border'}`}>
              <div className={`w-3 h-3 rounded-full bg-white transition-transform ${deep ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-xs text-korinth-muted font-mono text-center mb-4">Suggested questions:</p>
                {starters.map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)}
                    className="w-full text-left px-3 py-2 rounded-lg bg-korinth-bg border border-korinth-border/50 text-xs font-mono text-korinth-text hover:border-korinth-accent/50 hover:text-korinth-accent transition-colors">
                    <Sparkles className="w-3 h-3 inline mr-1.5 text-korinth-accent" />{q}
                  </button>
                ))}
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-xs leading-relaxed ${
                  msg.role === 'user' ? 'bg-korinth-accent/10 text-korinth-text border border-korinth-accent/20'
                    : 'bg-korinth-bg text-korinth-text border border-korinth-border/50'
                }`}>
                  {msg.role === 'assistant' && <Brain className="w-3 h-3 text-korinth-accent inline mr-1" />}
                  <span className="whitespace-pre-wrap">{renderContent(msg.content)}</span>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-korinth-bg border border-korinth-border/50 rounded-lg px-3 py-2">
                  <Brain className={`w-3 h-3 inline mr-1 animate-pulse ${deep ? 'text-purple-400' : 'text-korinth-accent'}`} />
                  <span className="text-xs text-korinth-muted font-mono">{deep ? 'KAIROS thinking deeply...' : 'Thinking...'}</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-korinth-border">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} className="flex gap-2">
              <input type="text" value={input} onChange={e => setInput(e.target.value)}
                placeholder="Ask KAIROS anything..."
                className={`flex-1 px-3 py-2 bg-korinth-bg rounded-lg text-xs font-mono text-white placeholder-korinth-muted/50 outline-none border ${deep ? 'border-purple-500/50 focus:border-purple-400' : 'border-korinth-border focus:border-korinth-accent'}`}
                disabled={loading} />
              <button type="submit" disabled={loading || !input.trim()}
                className={`px-3 py-2 rounded-lg disabled:opacity-30 ${deep ? 'bg-purple-500 text-white' : 'bg-korinth-accent text-korinth-bg'}`}>
                <Send className="w-3.5 h-3.5" />
                {deep && <span className="text-[7px] block -mt-0.5">OPUS</span>}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
